'use client';

import { api } from '@/lib/api';
import { useAppDispatch } from '@/lib/redux/store';
import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { populateFromJobDetails } from '../redux/jobEditors.slice';
import { JobDetailsResponse } from '../types';

/**
 * Fetch job details from the API
 * @param jobId ID of the job to fetch details for
 */
const fetchJobDetails = async (jobId: string): Promise<JobDetailsResponse> => {
  const response = await api.get<JobDetailsResponse>(`/v2/p2p/${jobId}`);
  return response.data;
};

/**
 * Hook for fetching job details
 * @param jobId ID of the job to fetch details for
 */
export const useJobDetails = (jobId: string) => {
  return useQuery<JobDetailsResponse, Error>({
    queryKey: ['jobDetails', jobId],
    queryFn: () => fetchJobDetails(jobId),
    enabled: !!jobId,
  });
};

/**
 * Enhanced hook for fetching job details and initializing job editor state
 * @param jobId ID of the job to fetch details for
 */
export const useJobDetailsForEdit = (jobId: string) => {
  const jobDetailsQuery = useJobDetails(jobId);
  const dispatch = useAppDispatch();

  useEffect(() => {
    if (jobDetailsQuery.data) {
      dispatch(
        populateFromJobDetails({
          jobId,
          jobDetails: jobDetailsQuery.data,
        })
      );
    }
  }, [jobDetailsQuery.data, jobId, dispatch]);

  return jobDetailsQuery;
};
